import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronDown, Code, CheckCircle2, Download, ChevronRight, Cpu, Zap, ArrowRight } from "lucide-react";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import { ScrollReveal } from "../components/story/ScrollReveal";
import { SystemsCapabilities } from "../components/story/SystemsCapabilities";
import { MemoryVisualizer } from "../components/story/MemoryVisualizer";
import { CodeReveal, NarrativeCaption } from "../components/story";
import { useScrollContext } from "../context/ScrollContext";

const Section = ({ children, id, className = "", noBorder = false, "aria-labelledby": ariaLabelledby }) => {
  const prefersReduced = useReducedMotion();
  const transition = prefersReduced
    ? { opacity: { duration: 0.3 } }
    : { duration: 1, ease: [0.16, 1, 0.3, 1] };

  return (
    <motion.section
      id={id}
      aria-labelledby={ariaLabelledby}
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: prefersReduced ? 0 : 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={transition}
      className={`py-32 ${!noBorder ? 'border-t border-[var(--border-subtle)]' : ''} ${className}`}
    >
      {children}
    </motion.section>
  );
};

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Dopove",
      "url": "https://dopove.com",
      "logo": "https://dopove.com/logo_new_16x9_trans_inverted.png",
      "sameAs": [
        "https://x.com/dopove",
        "https://peerlist.io/dopove",
        "https://github.com/dopove",
        "https://linkedin.com/company/dopove"
      ],
      "foundingDate": "2024",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine."
    },
    {
      "@type": "SoftwareApplication",
      "name": "ICE (Indexless Context Engine)",
      "applicationCategory": "DeveloperApplication",
      "operatingSystem": "Cross-platform",
      "offers": {
        "@type": "OfferCatalog",
        "name": "ICE Licensing Tiers",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Community License",
            "description": "Free for individual, non-commercial, and personal research purposes",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": "https://dopove.com/auth",
            "eligibleRegion": {
              "@type": "GeoShape",
              "addressCountry": "WORLD"
            }
          },
          {
            "@type": "Offer",
            "name": "Enterprise Edition",
            "description": "High-performance regulatory, compliance, and training toolkit for production workloads",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": "mailto:info@dopove.com",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "priceCurrency": "USD",
              "minPrice": "0"
            },
            "eligibleRegion": {
              "@type": "GeoShape",
              "addressCountry": "WORLD"
            }
          }
        ]
      },
      "url": "https://dopove.com/ice",
      "description": "ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful. It runs inside your application process.",
      "featureList": [
        "Zero-Conf Local Mode with embedded memory",
        "Drop-In SDK Middleware (3 lines of code)",
        "Indexless Memory - no vector index management",
        "Compiled native core",
        "Type-Safe Pydantic-Validated API",
        "Enterprise Escape Hatches (middleware hooks)",
        "Memory Guardian (VRAM pressure monitoring)"
      ],
      "applicationSubCategory": "AI/ML Infrastructure",
      "license": "https://dopove.com/licensing",
      "downloadUrl": "https://dopove.com/download",
      "installUrl": "https://docs.dopove.com/docs/ice/quickstart",
      "softwareVersion": "3.1",
      "datePublished": "2024-01-15",
      "author": {
        "@type": "Organization",
        "name": "Dopove"
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is ICE?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ICE (Indexless Context Engine) is a persistent memory layer for LLMs that runs entirely within your application process. It provides zero-conf local mode with embedded memory, requiring no external databases or daemons."
          }
        },
        {
          "@type": "Question",
          "name": "How many lines of code to add ICE to my LLM?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Just 3 lines of code. Wrap your standard AsyncOpenAI or Anthropic Python client with ICE middleware."
          }
        },
        {
          "@type": "Question",
          "name": "Is ICE free for commercial use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Community License is free for individual, non-commercial, and personal research purposes. Commercial use requires an Enterprise Edition license with custom pricing."
          }
        },
        {
          "@type": "Question",
          "name": "Does ICE require Docker or PostgreSQL?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. ICE boots instantly with an Indexless memory file (~/.ice/ice_memory.db). No Docker, PostgreSQL, or daemons required."
          }
        }
      ]
    }
  ]
};

const FeatureCard = ({ icon: Icon, title, description, customDelay = 0 }) => {
  const prefersReduced = useReducedMotion();
  const transition = prefersReduced
    ? { opacity: { duration: 0.3 } }
    : { duration: 0.8, delay: customDelay, ease: [0.23, 1, 0.32, 1] };

  return (
    <motion.div
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: prefersReduced ? 0 : 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={transition}
      className="bg-[#23263a]/40 p-10 rounded-[2.5rem] border border-[#FFFFFF]/10 flex flex-col items-center text-center hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] backdrop-blur-sm cursor-pointer group"
    >
      <div className="bg-[#23263a] p-5 rounded-2xl mb-8 hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]">
        <Icon className="h-8 w-8 text-[#6FB6E8]" />
      </div>
      <h3 className="text-2xl font-bold mb-4 text-[#FFFFFF]">{title}</h3>
      <p className="text-[#FFFFFF]/70 leading-relaxed font-light">{description}</p>
    </motion.div>
  );
};

const MechanismCard = ({ item, idx }) => {
  const prefersReduced = useReducedMotion();
  const transition = prefersReduced
    ? { opacity: { duration: 0.3 } }
    : { duration: 0.8, delay: idx * 0.15, ease: [0.23, 1, 0.32, 1] };

  return (
    <motion.div
      key={idx}
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: prefersReduced ? 0 : 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={transition}
      className="flex flex-col space-y-6 p-8 rounded-3xl border border-white/5 bg-[#23263a]/10 hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] cursor-pointer group"
    >
      <span className="text-5xl font-black text-[#6FB6E8]/10">{item.num}</span>
      <h3 className="text-3xl font-bold text-[#FFFFFF]">{item.title}</h3>
      <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed">
        {item.desc}
      </p>
    </motion.div>
  );
};

export default function ICEPage() {
  const { scrollYProgressSpring } = useScrollContext();

  // Hero Orbs Component (defined before use)
  const ICEHeroOrbs = () => {
    const prefersReduced = useReducedMotion();

    if (prefersReduced) {
      return (
        <>
          <div
            className="absolute top-[10%] left-[10%] w-[30rem] h-[30rem] bg-[#6FB6E8]/10 rounded-full blur-[130px] pointer-events-none"
          />
          <div
            className="absolute bottom-[10%] right-[10%] w-[35rem] h-[35rem] bg-[#1E7FD1]/10 rounded-full blur-[150px] pointer-events-none"
          />
          <div
            className="absolute inset-0 bg-gradient-to-br from-[#6FB6E8]/20 to-transparent rounded-full blur-[100px] pointer-events-none"
          />
        </>
      );
    }

    return (
      <>
        <motion.div
          className="absolute top-[10%] left-[10%] w-[30rem] h-[30rem] bg-[#6FB6E8]/10 rounded-full blur-[130px] pointer-events-none"
          animate={{
            scale: [1, 1.1, 1],
            translateX: ["0%", "5%", "0%"],
            translateY: ["0%", "-3%", "0%"]
          }}
          transition={{ duration: 20, ease: "easeInOut", repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-[10%] right-[10%] w-[35rem] h-[35rem] bg-[#1E7FD1]/10 rounded-full blur-[150px] pointer-events-none"
          animate={{
            scale: [1, 1.15, 1],
            translateX: ["0%", "-4%", "0%"],
            translateY: ["0%", "3%", "0%"]
          }}
          transition={{ duration: 25, ease: "easeInOut", repeat: Infinity, repeatDelay: 2 }}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-[#6FB6E8]/20 to-transparent rounded-full blur-[100px] pointer-events-none"
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ duration: 8, ease: "easeInOut", repeat: Infinity }}
        />
      </>
    );
  };

  return (
    <div className="bg-[#191d2b] min-h-screen text-[#FFFFFF] flex flex-col font-sans selection:bg-[#6FB6E8]/30 overflow-x-hidden">
      <Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <title>ICE: turn-granular memory for AI agents | Dopove</title>
        <meta name="description" content="ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful. It runs inside your application process." />
        <meta property="og:title" content="ICE — Indexless Context Engine | Dopove" />
        <meta property="og:description" content="ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful. It runs inside your application process." />
        <meta property="og:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <meta property="og:url" content="https://dopove.com/ice" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ICE — Indexless Context Engine | Dopove" />
        <meta name="twitter:description" content="ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful. It runs inside your application process." />
        <meta name="twitter:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <link rel="canonical" href="https://dopove.com/ice" />
      </Helmet>


      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-20 text-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(111,182,232,0.1)_0%,transparent_50%)]" />

          <div className="relative z-10 w-full max-w-6xl mx-auto text-left mb-4">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'ICE' }]} />
          </div>

          <ICEHeroOrbs />

          <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center text-left">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <span className="text-sm font-mono tracking-[0.3em] text-[#6FB6E8]">ICE v3.1 🧊</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8 tracking-tight leading-[1.08] text-balance text-[#FFFFFF]"
              >
                Turn-granular memory <span className="text-[#6FB6E8]">for AI agents.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
                className="text-xl md:text-2xl text-[#FFFFFF]/70 font-light max-w-2xl leading-relaxed mb-24"
              >
                ICE, the Indexless Context Engine, is turn-granular memory for AI agents that makes the context you already have useful.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-6"
              >
                <Link to="/auth" className="px-12 py-5 rounded-full bg-[#1E7FD1] text-white font-bold tracking-tight text-center block hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)]">
                Get Started Free
              </Link>
                <Link to="/download" className="px-12 py-5 rounded-full border border-[#6FB6E8]/30 text-white font-bold tracking-tight text-center flex items-center justify-center gap-2 block hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)]">
                  <Download className="w-4 h-4" />
                  Download ICE v3.1
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="relative aspect-square flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#6FB6E8]/20 to-transparent rounded-full blur-[100px] animate-pulse-reduced" />
              <div
                className="w-full max-w-md flex items-center justify-center z-10 animate-logo-float"
              >
                <img
                  src="/ICE_1x1_trans.png"
                  alt="ICE Logo"
                  className="w-full h-auto object-contain drop-shadow-[0_0_50px_rgba(111,182,232,0.3)]"
                  style={{ filter: "hue-rotate(25deg) brightness(1.1)" }}
                />
              </div>
            </motion.div>
          </div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-12 opacity-30"
          >
            <ChevronDown className="h-8 w-8 text-[#FFFFFF]" />
          </motion.div>
        </section>

        {/* The Mechanism */}
        <ScrollReveal beatId="ice-mechanism" threshold={0.15} rootMargin="0px" data-beat-id="ice-mechanism">
          <Section id="how-it-works" className="bg-[#191d2b]" noBorder>
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid md:grid-cols-3 gap-12">
                {[
                  {
                    num: "01",
                    title: "Embedded.",
                    desc: "ICE runs entirely within your application process. No external servers, database daemons, or network round-trips required."
                  },
                  {
                    num: "02",
                    title: "Optimized.",
                    desc: "Core memory operations are handled by an optimized, compiled binary kernel, bypassing Python runtime overhead."
                  },
                  {
                    num: "03",
                    title: "Type-Safe.",
                    desc: "Surgical control over cognitive defaults, budgets, and query limits via a clean SDK validated with strict Pydantic models."
                  }
                ].map((item, idx) => (
        <MechanismCard
          key={idx}
          item={item}
          idx={idx}
        />
      ))}
              </div>
            </div>
          </Section>
        </ScrollReveal>

        <Section className="max-w-6xl mx-auto" aria-labelledby="ice-systems-heading">
          <SystemsCapabilities headingId="ice-systems-heading" />
        </Section>

        {/* Memory Visualizer - The Reveal */}
        <ScrollReveal beatId="ice-reveal" threshold={0.2} rootMargin="0px" data-beat-id="ice-reveal">
          <Section className="bg-[#151824] overflow-hidden" noBorder>
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#6FB6E8]/10 border border-[#6FB6E8]/20 rounded-full text-[#6FB6E8] text-[10px] font-black uppercase tracking-widest mb-6">
                    Memory Visualizer
                  </div>
                  <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#FFFFFF] mb-8">
                    Useful, not just long.
                  </h2>
                  <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed mb-8 max-w-xl">
                    While others chase longer contexts, we ask what makes the information in the window useful. ICE works on the signal-to-noise ratio of the context an agent already has.
                  </p>

                  <div className="space-y-4">
                    {[
                      "Runs inside your application process, with a zero-config local mode.",
                      "Isolated client environments per tenant",
                      "Pydantic-validated safe configuration model"
                    ].map((text, i) => (
                      <div key={i} className="flex items-center gap-3 text-[#FFFFFF]/80">
                        <CheckCircle2 className="w-5 h-5 text-[#6FB6E8]" />
                        <span className="font-medium text-sm">{text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <MemoryVisualizer beatId="ice-reveal" storedColor="#6FB6E8" recalledColor="#1E7FD1" />
                  <NarrativeCaption text="Session 1 → Session 47. Same memory." delay={4000} />
                </div>
              </div>
            </div>
          </Section>
        </ScrollReveal>

        {/* Developer First - The Spell */}
        <ScrollReveal beatId="ice-spell" threshold={0.15} rootMargin="0px" data-beat-id="ice-spell">
          <Section className="bg-[#151824] overflow-hidden">
            <div className="max-w-6xl mx-auto px-6">
             <div className="grid lg:grid-cols-[1fr_1.25fr] gap-10 lg:gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#6FB6E8]/10 border border-[#6FB6E8]/20 rounded-full text-[#6FB6E8] text-[10px] font-black uppercase tracking-widest">
                  The Spell
                </div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#FFFFFF]">
                  Zero-Conf.<br/>
                  <span className="text-[#6FB6E8]">Embedded Memory.</span>
                </h2>
                <p className="text-[#FFFFFF]/70 text-lg font-light leading-relaxed max-w-xl">
                  Boots instantly with an Indexless memory file (`~/.ice/ice_memory.db`). No Docker, no PostgreSQL, no daemons required. Just wrap your OpenAI client and get started.
                </p>

                <ul className="space-y-4">
                  {[
                    "100% Embedded (runs completely locally)",
                    "Optimized Binary Core (bypasses Python overhead)",
                    "Type-Safe Enterprise API (Pydantic validated)"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#FFFFFF]/80">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#6FB6E8]" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/download"
                  className="inline-flex items-center gap-2 text-[#6FB6E8] font-bold text-sm hover:gap-4 hover-scale-press focus-visible-ring transition-[gap,transform] duration-200 ease-[var(--ease-out)]"
                >
                  Download Distribution <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-[#6FB6E8]/5 blur-[80px] rounded-full group-hover:bg-[#6FB6E8]/10 transition-colors" />
                <div className="relative bg-[#0d1117] rounded-3xl border border-[#FFFFFF]/10 overflow-hidden shadow-2xl">
                  <div className="flex items-center justify-between px-6 py-4 bg-[#161b22] border-b border-[#FFFFFF]/5">
                    <span className="text-xs font-mono text-[#FFFFFF]/40 uppercase tracking-widest">main.py</span>
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/20" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/20" />
                    </div>
                  </div>
                  <CodeReveal
                    language="python"
                    lineDelay={300}
                    charDelay={10}
                    showLineNumbers={true}
                    showCursor={true}
                    beatId="ice-spell"
                    code={`import ice
import asyncio
import os

# 1. Set your signed license key
os.environ["ICE_LICENSE_JWT"] = "your_signed_jwt_here"

async def main():
    # 2. Ingest documents into memory
    await ice.ingest("s3://data-lake/architecture.pdf")

    # 3. Query with automatic memory retrieval
    response = await ice.query(
        prompt="Analyze this.",
        model="gpt-4o"
    )
    print(response)

asyncio.run(main())`}
                  />
                  <NarrativeCaption text="Three lines. That's the whole integration." delay={3000} />
                </div>
              </motion.div>
            </div>
          </div>
        </Section>
        </ScrollReveal>

        {/* Core Features - The Architecture */}
        <ScrollReveal beatId="ice-architecture" threshold={0.15} rootMargin="0px" data-beat-id="ice-architecture">
          <Section className="bg-[#191d2b]">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-24">
                <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight text-[#FFFFFF]">Memory capabilities.</h2>
                <p className="text-[#FFFFFF]/70 text-xl font-light max-w-3xl mx-auto">Pure, embedded memory engine for Large Language Models. Mount virtual memory directly into your application.</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    icon: Cpu,
                    title: "Embedded & Private",
                    desc: "ICE runs entirely within your application process. No external servers, database daemons, or network round-trips required."
                  },
                  {
                    icon: Zap,
                    title: "High Performance Core",
                    desc: "Core memory operations are handled by an optimized, compiled binary kernel, bypassing Python runtime overhead."
                  },
                  {
                    icon: Code,
                    title: "Type-Safe SDK",
                    desc: "Surgical control over defaults, budgets, and query limits via a clean SDK validated with strict Pydantic models."
                  },
                  {
                    icon: Download,
                    title: "Enterprise Escape Hatches",
                    desc: "Inject your own logic before or after a query using middleware hooks for PII scrubbing, toxicity filtering, or custom audit logs."
                  },
                  {
                    icon: Cpu,
                    title: "Memory Guardian",
                    desc: "The scheduler automatically monitors VRAM pressure and down-quantizes precision to prevent out-of-memory system crashes."
                  }
                ].map((feat, featIdx) => (
                  <FeatureCard
                    key={featIdx}
                    icon={feat.icon}
                    title={feat.title}
                    description={feat.desc}
                    customDelay={featIdx * 0.1}
                  />
                ))}
              </div>
            </div>
          </Section>
        </ScrollReveal>

        {/* Integration */}
        <ScrollReveal beatId="ice-integration" threshold={0.15} rootMargin="0px" data-beat-id="ice-integration">
          <Section>
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#FFFFFF]">Seamless Integration.</h2>
                  <p className="text-[#FFFFFF]/70 text-xl font-light leading-relaxed">
                    Deploy ICE exactly how your stack demands. Compiled native core.
                  </p>
                </div>

                <div className="space-y-6">
                  <div
                    className="p-8 rounded-[2rem] bg-[#23263a]/50 border border-[#FFFFFF]/10 flex items-center gap-6 group hover:bg-[#23263a]/80 hover-lift focus-visible-ring transition-[background-color,border-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] cursor-pointer"
                  >
                    <div className="p-4 rounded-xl bg-[#6FB6E8]/10 hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)]">
                      <Code className="w-8 h-8 text-[#6FB6E8]" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-[#FFFFFF] mb-1">ICE for Python</h4>
                      <p className="text-[#FFFFFF]/60 text-sm font-light">Compiled SDK for AI engineering and LangChain.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </ScrollReveal>

        {/* Pricing Section */}
        <ScrollReveal beatId="ice-pricing" threshold={0.15} rootMargin="0px" data-beat-id="ice-pricing">
          <Section className="bg-[#191d2b] py-24 border-t border-[#FFFFFF]/5" noBorder>
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#FFFFFF] mb-6">Simple Licensing</h2>
                <p className="text-xl text-[#FFFFFF]/60 max-w-3xl mx-auto font-light leading-relaxed">
                  Start free. Scale when you need to.
                </p>
              </div>

              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16 max-w-5xl mx-auto">
                {/* Community Edition */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                  className="p-8 rounded-[2rem] bg-[#23263a]/30 border border-[#FFFFFF]/10 flex flex-col relative hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] cursor-pointer"
                >
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-[#FFFFFF] mb-2 flex items-center gap-2">🧊 Community</h3>
                    <p className="text-[#FFFFFF]/60">Free for individuals and research. Local-first, zero-config.</p>
                  </div>
                  <div className="mb-8 pb-8 border-b border-[#FFFFFF]/10">
                    <span className="text-5xl font-bold text-[#FFFFFF]">Free</span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />Individual & research use</li>
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />Local-first, no cloud required</li>
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />Full core features</li>
                  </ul>
                  <Link to="/auth" className="w-full py-4 mt-auto rounded-xl bg-[#23263a] border border-[#FFFFFF]/10 text-white font-bold text-center hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] block">
                    Get Started
                  </Link>
                </motion.div>

                {/* Enterprise */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, delay: 0.15, ease: [0.23, 1, 0.32, 1] }}
                  className="p-8 rounded-[2rem] bg-[#23263a]/30 border border-[#FFFFFF]/10 flex flex-col relative hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] cursor-pointer"
                >
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#1E7FD1] text-white px-4 py-1 rounded-full text-sm font-bold tracking-wider uppercase">
                    Enterprise
                  </div>
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-[#FFFFFF] mb-2">Enterprise</h3>
                    <p className="text-[#FFFFFF]/60">For teams needing compliance, support, and scale.</p>
                  </div>
                  <div className="mb-8 pb-8 border-b border-[#FFFFFF]/10">
                    <span className="text-4xl font-bold text-[#FFFFFF]">Custom</span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />SSO, RBAC & audit logs</li>
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />Priority support & SLAs</li>
                    <li className="flex items-start text-[#FFFFFF]/80"><CheckCircle2 className="w-5 h-5 text-[#1E7FD1] mr-3 shrink-0" />Air-gapped deployment option</li>
                  </ul>
                  <a href="mailto:info@dopove.com" className="w-full py-4 mt-auto rounded-xl bg-[#1E7FD1] border border-[#1E7FD1] text-white font-bold text-center hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] block">
                    Contact Sales
                  </a>
                </motion.div>
              </div>
            </div>
          </Section>
        </ScrollReveal>

        {/* Architecture Diagram */}
        <ScrollReveal beatId="ice-architecture-diagram" threshold={0.15} rootMargin="0px" data-beat-id="ice-architecture-diagram">
          <Section className="bg-[#151824] py-24" noBorder>
            <div className="max-w-6xl mx-auto px-6">
              <div className="rounded-[2rem] overflow-hidden border border-[#FFFFFF]/10 bg-[#191d2b]/50 backdrop-blur-sm p-4">
                <img
                  src="/ICE - Indexless Context Engine.png"
                  alt="ICE Architecture Diagram"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>
          </Section>
        </ScrollReveal>

        {/* CTA & Next Chapter */}
        <ScrollReveal beatId="ice-cta" threshold={0.15} rootMargin="0px" data-beat-id="ice-cta">
          <Section className="bg-[#191d2b] py-40" noBorder>
            <div className="max-w-4xl mx-auto px-6 text-center space-y-12">
              <h2 className="text-5xl md:text-7xl font-bold tracking-tight text-[#FFFFFF]">Built for Production.</h2>
              <p className="text-[#FFFFFF]/60 text-xl md:text-2xl font-light leading-relaxed">
                If you are shipping persistent enterprise copilots or multi-tenant AI products, stop rebuilding brittle Postgres/Redis memory workarounds. Drop ICE into your stack today.
              </p>
              <div className="flex justify-center">
                <a href="mailto:info@dopove.com" className="px-16 py-6 rounded-full bg-[#1E7FD1] text-white font-bold hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] uppercase tracking-widest text-sm">
                  Talk to us
                </a>
              </div>

              {/* Next Chapter CTA */}
              <div className="pt-16 border-t border-[#FFFFFF]/10">
                <div className="inline-flex items-center gap-4 px-6 py-4 rounded-full bg-[#23263a]/50 border border-[#6FB6E8]/30">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#6FB6E8]">Next Chapter</span>
                  <Link
                    to="/goingmerry"
                    className="flex items-center gap-2 text-[#FFFFFF] font-medium hover:gap-4 hover-scale-press focus-visible-ring transition-[gap,transform,color] duration-200 ease-[var(--ease-out)]"
                  >
                    <span>Chapter 2: Velocity</span>
                    <ArrowRight className="w-5 h-5 text-[#6FB6E8]" />
                  </Link>
                </div>
                <p className="mt-4 text-[#FFFFFF]/50 text-sm font-light max-w-xl mx-auto">
                  GoingMerry is the next product we are building after ICE.
                </p>
              </div>
            </div>
          </Section>
        </ScrollReveal>
      </main>

      <Footer />
    </div>
  );
}
